import { useState, useCallback, useRef } from 'react';
import { useSessionStore } from './useSession';

const LOVENSE_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const LOVENSE_WRITE_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';

export function useLovense() {
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [battery, setBattery] = useState<number | undefined>();
  const characteristicRef = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const deviceRef = useRef<BluetoothDevice | null>(null);

  const { session } = useSessionStore();

  const connect = useCallback(async () => {
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'Lovense' }],
        optionalServices: [LOVENSE_SERVICE_UUID]
      });

      deviceRef.current = device;
      setDeviceName(device.name || 'Lovense Toy');

      const server = await device.gatt?.connect();
      const service = await server?.getPrimaryService(LOVENSE_SERVICE_UUID);
      const characteristic = await service?.getCharacteristic(LOVENSE_WRITE_UUID);

      characteristicRef.current = characteristic || null;
      setConnected(true);

      device.addEventListener('gattserverdisconnected', () => {
        setConnected(false);
        characteristicRef.current = null;
      });

    } catch (error) {
      console.error('Bluetooth connection failed:', error);
    }
  }, []);

  const sendCommand = useCallback((command: string) => {
    if (!characteristicRef.current) return;
    const encoder = new TextEncoder();
    characteristicRef.current.writeValue(encoder.encode(command + ';'));
  }, []);

  const vibrate = useCallback((intensity: number) => {
    const maxIntensity = session?.consent.maxIntensity || 20;
    const safeIntensity = Math.max(0, Math.min(maxIntensity, intensity));
    sendCommand(`Vibrate:${safeIntensity}`);
  }, [sendCommand, session]);

  const setPattern = useCallback((pattern: string) => {
    const patterns = ['Pulse', 'Wave', 'Fireworks', 'Earthquake'];
    if (patterns.includes(pattern)) {
      const maxIntensity = session?.consent.maxIntensity || 20;
      sendCommand(`Pattern:${pattern}:${Math.floor(maxIntensity / 2)}`);
    }
  }, [sendCommand, session]);

  const stop = useCallback(() => {
    sendCommand('Vibrate:0');
  }, [sendCommand]);

  return {
    connected,
    deviceName,
    battery,
    connect,
    vibrate,
    setPattern,
    stop
  };
}
